import { createRef, useEffect, useRef, useState } from 'react'

import Breadcrumb from '~/components/atoms/Breadcrumb/Breadcrumb'
import { urlFor } from '~/components/atoms/Image/Image'
import InPageSubNav from '~/components/report/components/InPageSubNav/InPageSubNav'
import InPageNav from '~/components/report/InPageNav/InPageNav'
import { resolveBlock } from '~/components/report/modules/serializers'
import StickyAdvert from '~/components/report/modules/StickyAdvert/StickyAdvert'
import type { PageNavProps } from '~/components/report/PageNav/PageNav'
import type { PageData } from '~/components/report/types'
import { ReportTheme, ThemeAccentContext } from '~/components/report/types'
import { ComponentTheme } from '~/components/themes'
import useMabPostHog, {
  PushInteractionLocation,
  PushInteractionType,
  PushWebProduct,
} from '~/hooks/useMabPostHog'
import { useZepic } from '~/hooks/useZepic'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'

import styles from '~/styles/pages.module.css'

type Position = {
  top: number
  bottom: number
  left: number
  key?: string
}

export default function PageBuilderLayout(props: PageData) {
  const {
    inPageNav,
    inPageNavTheme,
    intro,
    body,
    breadcrumb,
    advert,
    title,
    leadImage,
    url,
  } = props

  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)
  const refs = useRef(body?.map(() => createRef<HTMLDivElement>()) || [])
  const introRefs = useRef(intro?.map(() => createRef<HTMLDivElement>()) || [])
  const [introPositions, setIntroPositions] = useState<Position[]>([])
  const [positions, setPositions] = useState<Position[]>([])
  const { pushEvent } = useMabPostHog()
  const { track: zepicTrack } = useZepic()

  useEffect(() => {
    pushEvent('Online Report viewed', {
      interactionLocation: PushInteractionLocation.Reports,
      interactionType: PushInteractionType.PageLoad,
      webProduct: PushWebProduct.Reports,
    })

    if (leadImage) {
      const zepObj = {
        pageurl: `https://www.myartbroker.com/market-reports${url}`,
        title: title,
        image: urlFor(leadImage.asset, 800),
      }
      zepicTrack('report_downloaded', zepObj)
    }
  }, [])

  useEffect(() => {
    if (!intro) return

    const pos: Position[] = introRefs.current.map((ref, index) => {
      const rect = ref.current?.getBoundingClientRect()
      return {
        top: rect ? rect.top + window.scrollY : 0,
        left: rect ? rect.left + window.scrollX : 0,
        bottom: rect ? rect.bottom + window.scrollY : 0,
        key: intro[index]._key,
      }
    })

    setIntroPositions(pos)
  }, [intro])

  useEffect(() => {
    if (!body) return

    const pos: Position[] = refs.current.map((ref, index) => {
      const rect = ref.current?.getBoundingClientRect()
      return {
        top: rect ? rect.top + window.scrollY : 0,
        left: rect ? rect.left + window.scrollX : 0,
        bottom: rect ? rect.bottom + window.scrollY : 0,
        key: body[index]._key,
      }
    })

    setPositions(pos)
  }, [body])

  if (!body || !intro) return null

  const mainHeaderItems = body
    .map((item: any) => {
      if (item?.useAsDivider && item.moduleId) {
        return {
          title: item.title,
          slug: item.moduleId,
        }
      }
    })
    .filter(x => x !== undefined)

  /**
   * Group items by dividers so modules are grouped within their relevent sections
   */
  function groupBySection(items: any) {
    const grouped: any = {}
    let currentSectionKey: any = null
    let firstSlideProcessed = false

    items.forEach((item: any) => {
      if ('useAsDivider' in item && item.useAsDivider) {
        currentSectionKey = item._key

        grouped[currentSectionKey] = {
          title: item.title,
          items: [],
          // use the position to determine when to show the navigation
          position:
            (positions.find(_item => _item.key === item._key)?.top || 0) -
            (!firstSlideProcessed
              ? isMobile
                ? 900
                : 148
              : isMobile
                ? 1200
                : 328), // for some reason the first one has a different calulcaiton of the position than the rest.
        }
        if (!firstSlideProcessed) firstSlideProcessed = true
      } else if (currentSectionKey) {
        // Only group the artist intro's as these are the sub groupings of the internal
        if (item._type === 'reportArtistIntro') {
          grouped[currentSectionKey].items.push({
            title: item.title,
            slug: item.moduleId,
            position:
              (positions.find(_item => _item.key === item._key)?.top || 0) -
              (isMobile ? 500 : 300),
          })
        }
      }
    })

    return grouped
  }

  const groupedSubNavigations = groupBySection(body)

  let navItems: PageNavProps['items'] = []

  if (intro.length > 0) {
    navItems.push({
      title: 'intro',
      slug: 'intro',
    })
  }
  navItems = [...navItems, ...mainHeaderItems] as PageNavProps['items']

  return (
    <ThemeAccentContext.Provider
      value={{
        themeAccent: props.themeAccent || null,
        themeAccentText:
          props.themeAccentText && props.themeAccentText === 'light'
            ? '255, 255, 255'
            : '10, 10, 10',
      }}
    >
      <>
        {/* Been removed for now - keeping incase added back in */}
        {/* <Header
          defaultTitle={navCenterTitle}
          menuTheme={navSlideoutTheme}
          theme={mainNavTheme && mainNavTheme}
          pageItems={navItems || []}
          subNavItems={groupedSubNavigations}
        /> */}

        <div className={styles.reportContainer}>
          <Breadcrumb list={breadcrumb} theme={ComponentTheme.primary} />
          {intro && (
            <div className={styles.introContainer}>
              {intro.map((item, x) => {
                if (x === 0) {
                  return (
                    <>
                      {resolveBlock(item, x, advert?.image)}
                      {inPageNav && (
                        <InPageNav
                          items={navItems || []}
                          theme={inPageNavTheme || ReportTheme.primary}
                        />
                      )}
                    </>
                  )
                }
                return (
                  <>
                    {x === 1 && advert?.image && (
                      <StickyAdvert
                        {...advert}
                        isSticky
                        stickyPosition={introPositions[x]?.top}
                        stickyEndPosition={
                          introPositions[introPositions.length - 1]?.top
                        }
                      />
                    )}
                    <div
                      id={x === 1 ? 'intro' : undefined}
                      key={`report-intro-item-${x}`}
                      ref={introRefs.current[x]}
                    >
                      {resolveBlock(item, x)}
                    </div>
                  </>
                )
              })}
            </div>
          )}
          {body &&
            body.map((item, x) => {
              {
                return (
                  <>
                    <div ref={refs.current[x]} key={`${item._type}-${x}`}>
                      {resolveBlock(item, x)}
                    </div>
                    {'useAsDivider' in item &&
                      item.useAsDivider &&
                      item?.moduleId && (
                        <InPageSubNav
                          {...groupedSubNavigations[item?._key]}
                          moduleId={item._key}
                          items={groupedSubNavigations[item?._key]?.items || []}
                          subNavItems={groupedSubNavigations}
                        />
                      )}
                  </>
                )
              }
            })}
        </div>
      </>
    </ThemeAccentContext.Provider>
  )
}
